import { DocumentNode, gql } from '@apollo/client';
import React from 'react';
import {
	InvoiceEditButtonBar,
	InvoiceEditButtonBarProps,
} from './InvoiceEditButtonBar';

import {
	useInvoiceEdit_CreateDraftInvoiceMutation,
	useInvoiceEdit_UpdateInvoiceMutation,
	useInvoiceEdit_RequestInvoiceAttachmentUploadUrlLazyQuery,
	InvoiceEditInvoiceFragment,
} from '#types';
import {
	InvoiceEditPresentation,
	InvoiceEditPresentationProps,
} from './InvoiceEditPresentation';
import { InvoiceEditLineItems } from './InvoiceEditLineItems';

import {
	InvoiceEditNotesAndLineItemSummary,
	InvoiceEditNotesAndLineItemSummaryProps,
} from './InvoiceEditNotesAndLineItemSummary';
import { contactsContextContactFragment } from '../../context/ContactsContextProvider/ContactsContextProvider';
import { InvoiceEditPaymentInstallments } from './InvoiceEditPaymentInstallments';
import {
	useNotificationGroupEmailNotificationGroupFragment,
	UseNotificationGroupHookResults,
	useNotificationGroupOrganizationFragment,
} from '../NotificationGroup/useNotificationGroup';
import { Paper, Typography } from '@mui/material';
import styles from './InvoiceEdit.module.scss';
import {
	InvoiceEditSettings,
	InvoiceEditSettingsProps,
} from './InvoiceEditSettings';
import { useInvoiceEdit } from './useInvoiceEdit';
import { UnsavedChangesModal } from '../UnsavedChangesModal';
import { Differences } from '../../lib/helpers/deepCompareAndDiff';

const invoiceEditInvoiceFragment = gql`
	fragment invoiceEditInvoice on Invoice {
		id
		allowsGratuity
		automatedRemindersEnabled
		eventDate
		uploadAttachment
		...invoiceEditButtonBarInvoice
		...invoiceEditPresentationInvoice
		...invoiceEditLineItemsInvoice
		...invoiceEditPaymentInstallmentsInvoice
		...invoiceEditNotesAndLineItemSummaryInvoice
		...invoiceEditSettingsInvoice
		contact {
			...contactsContextContact
		}
		emailNotificationGroup {
			...useNotificationGroupEmailNotificationGroup
		}
		organization {
			...useNotificationGroupOrganization
		}
	}
	${ InvoiceEditButtonBar.fragments.invoiceEditButtonBarInvoiceFragment }
	${ InvoiceEditPresentation.fragments.invoiceEditPresentationInvoiceFragment }
	${ InvoiceEditLineItems.fragments.invoiceEditLineItemsInvoiceFragment }
	${ InvoiceEditPaymentInstallments.fragments
		.invoiceEditPaymentInstallmentsInvoiceFragment }
	${ InvoiceEditNotesAndLineItemSummary.fragments
		.invoiceEditNotesAndLineItemSummaryInvoiceFragment }
	${ InvoiceEditSettings.fragments.invoiceEditSettingsInvoiceFragment }
	${ contactsContextContactFragment }
	${ useNotificationGroupEmailNotificationGroupFragment }
	${ useNotificationGroupOrganizationFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceEdit_createDraftInvoice = gql`
	mutation invoiceEdit_createDraftInvoice($data: InvoiceCreateInput!) {
		createDraftInvoice(data: $data) {
			id
			status
			eventDate
			...invoiceEditInvoice
		}
	}
	${ invoiceEditInvoiceFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceEdit_updateInvoice = gql`
	mutation invoiceEdit_updateInvoice(
		$data: InvoiceUpdateInput!
		$where: InvoiceWhereUniqueInput!
	) {
		updateInvoice(data: $data, where: $where) {
			...invoiceEditInvoice
		}
	}
	${ invoiceEditInvoiceFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const requestInvoiceAttachmentUploadUrl = gql`
	query InvoiceEdit_requestInvoiceAttachmentUploadUrl(
		$data: RequestAttachmentUploadUrlInput!
		$where: InvoiceWhereUniqueInput!
	) {
		requestInvoiceAttachmentUploadUrl(data: $data, where: $where) {
			uploadURL
		}
	}
`;

export type InvoiceEditProps = {
	invoiceId?: string;
	invoice?: Omit<InvoiceEditInvoiceFragment, 'id'> & { id?: string };
	setInvoice: React.Dispatch<
		React.SetStateAction<
			( Omit<InvoiceEditInvoiceFragment, 'id'> & { id?: string } ) | undefined
		>
	>;
	contactId?: string;
	isVisible: boolean;
	fileOrFilename?: File | string | undefined;
	setFileOrFilename: React.Dispatch<
		React.SetStateAction<File | string | undefined>
	>;
	notificationGroupProps: UseNotificationGroupHookResults;
	skipInstallmentCalculation?: boolean;
	setSkipInstallmentCalculation: React.Dispatch<React.SetStateAction<boolean>>;
	invoiceEdits?: Differences;
} & Pick<
	InvoiceEditButtonBarProps,
	'openCustomizeEmailModal' | 'setOpenCustomizeEmailModal' | 'initialStateReady'
>;

type InvoiceEditPropsWithFragments = React.FC<InvoiceEditProps> & {
	fragments: {
		invoiceEditInvoiceFragment: DocumentNode;
	};
};

export const InvoiceEdit: InvoiceEditPropsWithFragments = ( {
	invoiceId,
	invoice,
	setInvoice,
	contactId, // the contact from the bundle, if such a contact exists (we might just inject this into the contacts context)
	isVisible,
	fileOrFilename,
	setFileOrFilename,
	notificationGroupProps,
	openCustomizeEmailModal,
	setOpenCustomizeEmailModal,
	skipInstallmentCalculation = false,
	setSkipInstallmentCalculation,
	invoiceEdits,
	initialStateReady,
} ) => {
	const [ requestInvoiceAttachmentUploadUrl ] =
		useInvoiceEdit_RequestInvoiceAttachmentUploadUrlLazyQuery();
	const [ createDraftInvoice ] = useInvoiceEdit_CreateDraftInvoiceMutation();
	const [ updateInvoice ] = useInvoiceEdit_UpdateInvoiceMutation();

	const {
		invoiceTemplate,
		handleSetInvoiceTemplate,
		titleValidation,
		eventDateValidation,
		paymentInstallmentsSelectValidation,
		validationEnabledRef,
		validate,
		lineItemsDetails,
		onDragEnd,
		onAddLineItem,
		onRemoveLineItem,
		onUpdateLineItem,
		onBlurLineItem,
		isUploading,
		fileValidation,
		paymentInstallmentsDetails,
		onAddPaymentInstallments,
		onRemovePaymentInstallment,
		onUpdatePaymentInstallment,
		onBlurPaymentInstallment,
		buttonsAreDisabled,
		createOrUpdateInvoiceHandler,
		bundle,
		intentToChangeOrDeleteCurrentAttachmentFile,
		setIntentToChangeOrDeleteCurrentAttachmentFile,
		contactOptional,
		setContactOptional,
		unsavedChangesModalOpen,
		isSaving,
		handleModalSave,
		handleModalNavigate,
		handleModalClose,
	} = useInvoiceEdit( {
		invoiceId,
		invoice,
		setInvoice,
		contactId,
		requestInvoiceAttachmentUploadUrl,
		createDraftInvoice,
		updateInvoice,
		fileOrFilename,
		setFileOrFilename,
		notificationGroupProps,
		skipInstallmentCalculation,
		setSkipInstallmentCalculation,
		invoiceEdits,
	} );
	return (
		<>
			<InvoiceEditPresentation
				isVisible={ isVisible }
				invoice={ invoice }
				setInvoice={ setInvoice as InvoiceEditPresentationProps['setInvoice'] }
				invoiceExists={ !!invoiceId }
				invoiceTemplate={ invoiceTemplate }
				setInvoiceTemplate={ handleSetInvoiceTemplate }
				titleValidation={ titleValidation }
				onBlur={ () => validate( false ) }
				validationEnabled={ validationEnabledRef.current === true }
				eventDateValidation={ eventDateValidation }
				contactOptional={ contactOptional }
			>
				<InvoiceEditSettings
					invoice={ invoice }
					setInvoice={ setInvoice as InvoiceEditSettingsProps['setInvoice'] }
					notificationGroupProps={ notificationGroupProps }
				/>
			</InvoiceEditPresentation>
			<Paper className={ styles.lowerPaper }>
				<Typography variant='h6'>Invoice line items</Typography>
				<InvoiceEditLineItems
					/* invoiceId to use as an indicator if the URL has an ID in it or not. We
					could have checks down in the various bits but we already have this data
					so lets use it */
					invoiceId={ invoiceId }
					invoice={ invoice }
					lineItemsDetails={ lineItemsDetails }
					onDragEnd={ onDragEnd }
					onAddLineItem={ onAddLineItem }
					onRemoveLineItem={ onRemoveLineItem }
					onUpdateLineItem={ onUpdateLineItem }
					onBlur={ onBlurLineItem }
				/>
				<InvoiceEditNotesAndLineItemSummary
					invoice={ invoice }
					setInvoice={
						setInvoice as InvoiceEditNotesAndLineItemSummaryProps['setInvoice']
					}
					filename={
						fileOrFilename instanceof File
							? fileOrFilename?.name
							: fileOrFilename
					}
					isUploading={ isUploading }
					file={ fileOrFilename }
					setFile={ ( file?: File ) => setFileOrFilename( file ) }
					fileValidation={ fileValidation }
					intentToChangeOrDeleteCurrentAttachmentFile={
						intentToChangeOrDeleteCurrentAttachmentFile
					}
					setIntentToChangeOrDeleteCurrentAttachmentFile={
						setIntentToChangeOrDeleteCurrentAttachmentFile
					}
				/>
			</Paper>
			<InvoiceEditPaymentInstallments
				invoice={ invoice }
				/* invoiceId to use as an indicator if the URL has an ID in it or not. We
				could have checks down in the various bits but we already have this data
				so lets use it */
				invoiceExists={ !!invoiceId }
				paymentInstallmentsDetails={ paymentInstallmentsDetails }
				onAddPaymentInstallments={ onAddPaymentInstallments }
				onRemovePaymentInstallment={ onRemovePaymentInstallment }
				onUpdatePaymentInstallment={ onUpdatePaymentInstallment }
				onBlur={ onBlurPaymentInstallment }
				errorText={ paymentInstallmentsSelectValidation }
			/>
			<InvoiceEditButtonBar
				invoice={ invoice }
				onCreateOrUpdateHandler={ createOrUpdateInvoiceHandler }
				disabled={ buttonsAreDisabled }
				bundle={ bundle }
				setContactOptional={ setContactOptional }
				openCustomizeEmailModal={ openCustomizeEmailModal }
				setOpenCustomizeEmailModal={ setOpenCustomizeEmailModal }
				setSkipInstallmentCalculation={ setSkipInstallmentCalculation }
				validate={ validate }
				initialStateReady={ initialStateReady }
			/>
			<UnsavedChangesModal
				open={ unsavedChangesModalOpen }
				onClose={ handleModalClose }
				onSave={ handleModalSave }
				onNavigate={ handleModalNavigate }
				isSaving={ isSaving }
			/>
		</>
	);
};

InvoiceEdit.fragments = {
	invoiceEditInvoiceFragment,
};
